// What the editor writes for connectors (AB#200): a new connector, an end glued, re-glued or freed,
// an elbow's middle moved, the route switched, and the freeze a deletion makes. Each is ONE Y
// transaction under the caller's origin (LOCAL_ORIGIN by default: one step of the member's undo
// stack, which restores the bindings with the rest). The geometry is connectors.ts's.
//
// A bound end is never written as a point: its site is the truth, resolved at render time. The
// stored box (lineBox of the two ends, in the connector's frame) is kept as the fallback the model
// and the AI's restricted HTML use for an end that is, or becomes, free.
import * as Y from 'yjs'
import { lineBox, type PlacedBox } from '../lib/slide-presets'
import {
  LOCAL_ORIGIN,
  addElement,
  elementsOf,
  isConnector,
  round,
  slideMap,
  snapshotSlide,
  type Binding,
  type ElementStyle,
  type NewElement,
  type SlideSnapshot,
} from './model'
import { connectorsOn, facingSites, unbindEnds, withEnd, type SiteHit } from './connectors'

export type ConnectorGeom = 'line' | 'elbow'

/** A new connector's look: a dark line ending in an arrow (PowerPoint's arrow connector). */
export const CONNECTOR_STYLE: ElementStyle = { line: 'dk1', lineWidth: 2, lineTail: 'arrow' }

/** One end of a connector being drawn or re-glued: a site (glued), or a free slide point. */
export type EndAt = SiteHit | { x: number; y: number }

const bindingOf = (e: EndAt): Binding | undefined => ('el' in e ? { el: e.el, site: e.site } : undefined)

/** A new top-level connector between two ends (slide coordinates), glued where they are sites. */
export function newConnector(geom: ConnectorGeom, start: EndAt, end: EndAt, style: ElementStyle = CONNECTOR_STYLE): NewElement {
  const from = bindingOf(start)
  const to = bindingOf(end)
  return { kind: 'shape', geom, ...lineBox(start, end), style: { ...style }, ...(from ? { from } : {}), ...(to ? { to } : {}) }
}

function writeLine(m: Y.Map<unknown>, box: PlacedBox): void {
  for (const k of ['x', 'y', 'w', 'h', 'rot'] as const) {
    const v = round(box[k])
    if (m.get(k) !== v) m.set(k, v)
  }
}

/**
 * Glue an end of connector `id` to a site, or free it at a slide point (a drag of its handle ending
 * on a shape, or on empty canvas). The other end stays as it is. False when `id` is no connector.
 */
export function setConnectorEnd(doc: Y.Doc, s: SlideSnapshot, id: string, which: 'from' | 'to', at: EndAt, origin: unknown = LOCAL_ORIGIN): boolean {
  const conn = s.elements[id]
  if (!conn || !isConnector(conn)) return false
  const next = withEnd(s, conn, which, at)
  doc.transact(() => {
    const m = elementsOf(slideMap(doc, s.id)).get(id)
    if (!m) return
    writeLine(m, next)
    const b = next[which]
    if (b) m.set(which, b)
    else if (m.has(which)) m.delete(which)
  }, origin)
  return true
}

/** "Detach start / end": the end is freed where it is now. False when it was not glued. */
export function detachEnd(doc: Y.Doc, s: SlideSnapshot, id: string, which: 'from' | 'to', origin: unknown = LOCAL_ORIGIN): boolean {
  const conn = s.elements[id]
  const b = conn?.[which]
  if (!conn || !b) return false
  const next = unbindEnds(s, conn, (x) => x === b)
  if (!next) return false
  doc.transact(() => {
    const m = elementsOf(slideMap(doc, s.id)).get(id)
    if (!m) return
    writeLine(m, next.box)
    m.delete(which)
  }, origin)
  return true
}

/** An elbow's middle segment, where the member dragged it (null: back to the default route). */
export function setElbowAdjust(doc: Y.Doc, slideId: string, id: string, adj: number | null, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => {
    const m = elementsOf(slideMap(doc, slideId)).get(id)
    if (!m || m.get('geom') !== 'elbow') return
    if (adj === null) {
      if (m.has('adj')) m.delete('adj')
    } else if (m.get('adj') !== Math.round(adj)) m.set('adj', Math.round(adj))
  }, origin)
}

/** The inspector's route: straight (`line`) or `elbow`, for the selected connectors (a line drops its adjust). */
export function setRoute(doc: Y.Doc, slideId: string, ids: string[], geom: ConnectorGeom, origin: unknown = LOCAL_ORIGIN): void {
  doc.transact(() => {
    const els = elementsOf(slideMap(doc, slideId))
    for (const id of ids) {
      const m = els.get(id)
      if (!m || m.get('kind') !== 'shape' || (m.get('geom') !== 'line' && m.get('geom') !== 'elbow')) continue
      if (m.get('geom') !== geom) m.set('geom', geom)
      if (geom === 'line' && m.has('adj')) m.delete('adj')
    }
  }, origin)
}

/**
 * Before elements are deleted (`gone`: them and their members): every connector glued to one of them
 * — and not deleted with them — loses that binding, its end FROZEN where it resolves now, so it never
 * dangles (the server's freezeConnectors, the same repair). Runs inside the caller's transaction.
 */
export function freezeConnectors(doc: Y.Doc, slideId: string, gone: ReadonlySet<string>, origin: unknown = LOCAL_ORIGIN): void {
  const s = snapshotSlide(doc, slideId)
  const els = elementsOf(slideMap(doc, slideId))
  doc.transact(() => {
    for (const conn of connectorsOn(s, gone)) {
      if (gone.has(conn.id)) continue
      const next = unbindEnds(s, conn, (b) => gone.has(b.el))
      const m = els.get(conn.id)
      if (!next || !m) continue
      writeLine(m, next.box)
      for (const end of ['from', 'to'] as const) if (!next[end] && m.has(end)) m.delete(end)
    }
  }, origin)
}

/**
 * The keyboard's Connect (two elements selected): a straight connector between their facing sites,
 * on top of the slide. Returns its id, or null when either cannot take a connector.
 */
export function connectElements(doc: Y.Doc, s: SlideSnapshot, a: string, b: string, origin: unknown = LOCAL_ORIGIN): string | null {
  const ea = s.elements[a]
  const eb = s.elements[b]
  if (!ea || !eb || a === b || isConnector(ea) || isConnector(eb)) return null
  const pair = facingSites(s, a, b)
  if (!pair) return null
  return addElement(doc, s.id, newConnector('line', pair.from, pair.to), undefined, undefined, origin)
}
